import { useSelector } from 'react-redux';

import { useCapability } from '../instances/useInstance';
import type { InstanceCapabilities } from '../instances/capabilities';
import type { RootState } from '../store/store';
import type { ThemeMode } from '../types/User';
import { useSystemTheme } from './useSystemTheme';

/**
 * Whether a signed-in student's app renders dark. An instance that forces a
 * theme wins over the student's stored choice, which stays untouched (the
 * student gets it back if they leave the instance); otherwise an explicit
 * choice wins, and "system" follows the OS.
 */
export const resolveIsDarkTheme = ({
  storedTheme,
  systemTheme,
  forcedTheme,
}: {
  storedTheme: ThemeMode | undefined;
  systemTheme: ThemeMode;
  forcedTheme: InstanceCapabilities['forcedTheme'];
}): boolean => {
  if (forcedTheme === 'dark') return true;
  if (forcedTheme === 'light') return false;

  return (
    storedTheme === 'dark' ||
    (storedTheme === 'system' && systemTheme === 'dark')
  );
};

/**
 * `resolveIsDarkTheme` for the signed-in student. False with no profile: a
 * signed-out page picks its own surface.
 */
export const useIsDarkTheme = (): boolean => {
  const profile = useSelector(
    (state: Partial<RootState>) => state.user?.profile
  );

  const systemTheme = useSystemTheme();
  const forcedTheme = useCapability('forcedTheme');

  if (!profile) return false;

  return resolveIsDarkTheme({
    storedTheme: profile.theme,
    systemTheme,
    forcedTheme,
  });
};
